import React, { useState } from 'react';
import './ReportSourcesView.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import './App.css';

// Interfaces
export interface DocumentDetails {
    _id: string;
    extracted_text: string;
    filename: string;
    url?: string;
    is_pdf?: boolean;
    content_ref?: string;
    token_size?: number;
    update_timestamp?: string;
    extracted_timestamp?: string;
    checksum?: string;
    extraction_error?: string;
    download_timestamp?: string;
    metadata?: {
        title?: string;
        author?: string;
        date?: string;
        document_type?: string;
        language?: string;
        subject?: string;
        keywords?: string[];
    };
    metadata_validation_details?: {
        title?: {
            field: string;
            value: string;
            found: boolean;
            confidence: number;
            matches?: any[];
        };
        author?: {
            field: string;
            value: string;
            found: boolean;
            confidence: number;
            matches?: any[];
        };
        date?: {
            field: string;
            value: string;
            found: boolean;
            confidence: number;
            matches?: any[];
        };
        keywords?: {
            field: string;
            value: string[];
            found: boolean;
            confidence: number;
            matches?: any[];
        };
        subject?: {
            field: string;
            value: string;
            found: boolean;
            confidence: number;
            matches?: any[];
        };
    };
    metadata_validated?: string;

    // Classification data
    classification_experiment_id?: string;
    classification_results?: {
        plan: string;
        category: string[];
        class_probabilities: number[];
        short_description: string;
        reasoning: string[];
        language: string;
        title: string;
        author: string;
        confidence: number;
    };
    classification_update_timestamp?: string;

    // Experiments
    experiments?: Array<{
        experiment_id: string;
        model: string;
        update_timestamp: string;
    }>;

    // Tags
    tags?: string[];
}

export interface SearchResultEntity {
    text?: string;
    document_id?: string;
    page?: number | string;
}

export interface SearchResult {
    _id?: string;
    entity: SearchResultEntity;
    distance?: number;
    source?: {
        _id: string;
        name: string;
        filename?: string;
        url?: string;
        token_size?: number;
        is_pdf?: boolean;
    };
}

interface DocumentListItem {
    _id: string;
    name?: string;
    filename?: string;
}

export interface ReportSourceDetailViewProps {
    documentId: string;
    documentTitle?: string;
    documentDetails?: DocumentDetails;
    documentText?: string;
    documentUrl?: string;
    isPdf?: boolean;
    documentChunks: SearchResult[];
    loadingDocument: boolean;
    clearSelectedDocument: () => void;
    allDocuments: DocumentListItem[];
    onSelectDocument: (docId: string) => void;
}

const ReportSourceDetailView: React.FC<ReportSourceDetailViewProps> = ({
    documentTitle = '',
    documentDetails,
    documentText = '',
    documentUrl = '',
    isPdf = false,
    loadingDocument = false,
    clearSelectedDocument,
}) => {
    // State for toggling metadata visibility
    const [showMetadata, setShowMetadata] = useState<boolean>(true);

    // Determine if we're in an iframe
    const isInIframe = () => {
        try {
            return window.self !== window.top;
        } catch (e) {
            return true;
        }
    };

    const formatDate = (dateString?: string) => {
        if (!dateString) return '-';
        try {
            return new Date(dateString).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return dateString;
        }
    };

    const getConfidenceBadgeStyle = (confidence: number) => {
        return {
            marginLeft: '8px',
            fontSize: '0.8em',
            backgroundColor: confidence >= 0.9 ? '#28a745' : confidence >= 0.7 ? '#ffc107' : '#dc3545',
            color: 'white',
            padding: '2px 6px',
            borderRadius: '12px',
            fontWeight: 'bold' as const,
            display: 'inline-block' as const
        };
    };

    // Removed inline styles - using CSS classes instead

    if (loadingDocument) {
        return (
            <div className="container">
                <div className="loading-container">
                    <div className="bonds-loader-flex">
                        <i className="fas fa-spinner fa-spin loader-icon" aria-label="Loading" />
                        <span className="loader-text">Loading document details...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={{ width: '100%', maxWidth: '100%', padding: '0 20px', backgroundColor: 'white', boxSizing: 'border-box', overflowX: 'hidden' }}>
            {/* Header Section */}
            <div className="detail-header" style={{ paddingTop: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px', marginBottom: '24px' }}>
                    <div style={{ flex: '1', minWidth: '300px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '12px' }}>
                            <button
                                className="back-button"
                                onClick={clearSelectedDocument}
                            >
                                <i className="fas fa-arrow-left"></i> Back
                            </button>
                            <h1 style={{ margin: 0, color: '#333' }}>
                                {documentTitle || documentDetails?.metadata?.title || documentDetails?.filename || 'Document Details'}
                            </h1>
                        </div>

                        {/* Tags Display */}
                        {documentDetails?.tags && documentDetails.tags.length > 0 && (
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginLeft: '110px' }}>
                                {documentDetails.tags.map((tag, idx) => (
                                    <span key={idx} style={{
                                        padding: '4px 10px',
                                        backgroundColor: '#e3f2fd',
                                        border: '1px solid #2196f3',
                                        borderRadius: '12px',
                                        fontSize: '13px',
                                        color: '#1976d2',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '4px'
                                    }}>
                                        <i className="fas fa-tag" style={{ fontSize: '11px' }}></i>
                                        {tag}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>

                    <div style={{ display: 'flex', gap: '12px' }}>
                        <button
                            className="btn-secondary-action"
                            onClick={() => setShowMetadata(!showMetadata)}
                        >
                            <i className="fas fa-info-circle"></i>
                            {showMetadata ? ' Hide Metadata' : ' Show Metadata'}
                        </button>
                        {documentUrl && (
                            <a
                                href={documentUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn-secondary-action"
                                style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}
                            >
                                <i className="fas fa-download"></i> Download
                            </a>
                        )}
                    </div>
                </div>
            </div>

            {/* Metadata Section (collapsible) */}
            {showMetadata && documentDetails && (
                <div style={{ backgroundColor: 'white', padding: '24px', borderRadius: '8px', border: '1px solid #e0e0e0', marginBottom: '20px', boxSizing: 'border-box' }}>
                    <h3 style={{ color: '#333', textAlign: 'left', marginBottom: '20px' }}>Document Metadata</h3>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, 1fr)',
                        maxWidth: '100%',
                        gap: '16px',
                        boxSizing: 'border-box'
                    }}>

                        {/* Basic Information */}
                        <div className="detail-item">
                            <div className="detail-label-small">Document ID</div>
                            <div className="detail-value-large" style={{ fontFamily: 'monospace', fontSize: '14px', wordBreak: 'break-all' }}>{documentDetails._id}</div>
                        </div>

                        {documentDetails.metadata?.title && (
                            <div className="detail-item">
                                <div className="detail-label-small">Title</div>
                                <div className="detail-value-large">
                                    {documentDetails.metadata.title}
                                    {documentDetails.metadata_validation_details?.title?.confidence !== undefined && (
                                        <span
                                            title={`Confidence: ${(documentDetails.metadata_validation_details.title.confidence * 100).toFixed(1)}%`}
                                            style={getConfidenceBadgeStyle(documentDetails.metadata_validation_details.title.confidence)}
                                        >
                                            {(documentDetails.metadata_validation_details.title.confidence * 100).toFixed(0)}%
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}

                        {documentDetails.metadata?.author && (
                            <div className="detail-item">
                                <div className="detail-label-small">Author</div>
                                <div className="detail-value-large">
                                    {documentDetails.metadata.author}
                                    {documentDetails.metadata_validation_details?.author?.confidence !== undefined && (
                                        <span
                                            title={`Confidence: ${(documentDetails.metadata_validation_details.author.confidence * 100).toFixed(1)}%`}
                                            style={getConfidenceBadgeStyle(documentDetails.metadata_validation_details.author.confidence)}
                                        >
                                            {(documentDetails.metadata_validation_details.author.confidence * 100).toFixed(0)}%
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}

                        {documentDetails.metadata?.date && (
                            <div className="detail-item">
                                <div className="detail-label-small">Date</div>
                                <div className="detail-value-large">
                                    {documentDetails.metadata.date}
                                    {documentDetails.metadata_validation_details?.date?.confidence !== undefined && (
                                        <span
                                            title={`Confidence: ${(documentDetails.metadata_validation_details.date.confidence * 100).toFixed(1)}%`}
                                            style={getConfidenceBadgeStyle(documentDetails.metadata_validation_details.date.confidence)}
                                        >
                                            {(documentDetails.metadata_validation_details.date.confidence * 100).toFixed(0)}%
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}

                        {documentDetails.metadata?.document_type && (
                            <div className="detail-item">
                                <div className="detail-label-small">Document Type</div>
                                <div className="detail-value-large">{documentDetails.metadata.document_type}</div>
                            </div>
                        )}

                        {documentDetails.metadata?.language && (
                            <div className="detail-item">
                                <div className="detail-label-small">Language</div>
                                <div className="detail-value-large">{documentDetails.metadata.language}</div>
                            </div>
                        )}

                        {documentDetails.metadata?.subject && (
                            <div className="detail-item">
                                <div className="detail-label-small">Subject</div>
                                <div className="detail-value-large">
                                    {documentDetails.metadata.subject}
                                    {documentDetails.metadata_validation_details?.subject?.confidence !== undefined && (
                                        <span
                                            title={`Confidence: ${(documentDetails.metadata_validation_details.subject.confidence * 100).toFixed(1)}%`}
                                            style={getConfidenceBadgeStyle(documentDetails.metadata_validation_details.subject.confidence)}
                                        >
                                            {(documentDetails.metadata_validation_details.subject.confidence * 100).toFixed(0)}%
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}

                        {documentDetails.metadata?.keywords && documentDetails.metadata.keywords.length > 0 && (
                            <div className="detail-item">
                                <div className="detail-label-small">Keywords</div>
                                <div className="detail-value-large">
                                    {documentDetails.metadata.keywords.join(', ')}
                                    {documentDetails.metadata_validation_details?.keywords?.confidence !== undefined && (
                                        <span
                                            title={`Confidence: ${(documentDetails.metadata_validation_details.keywords.confidence * 100).toFixed(1)}%`}
                                            style={getConfidenceBadgeStyle(documentDetails.metadata_validation_details.keywords.confidence)}
                                        >
                                            {(documentDetails.metadata_validation_details.keywords.confidence * 100).toFixed(0)}%
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}

                        {/*{documentDetails.filename && (*/}
                        {/*    <div className="detail-item">*/}
                        {/*        <div className="detail-label-small">Filename</div>*/}
                        {/*        <div className="detail-value-large">{documentDetails.filename}</div>*/}
                        {/*    </div>*/}
                        {/*)}*/}

                        {documentDetails.token_size !== undefined && (
                            <div className="detail-item">
                                <div className="detail-label-small">Token Size</div>
                                <div className="detail-value-large">{documentDetails.token_size.toLocaleString()} tokens</div>
                            </div>
                        )}

                        {documentDetails.is_pdf !== undefined && (
                            <div className="detail-item">
                                <div className="detail-label-small">File Type</div>
                                <div className="detail-value-large">{documentDetails.is_pdf ? 'PDF Document' : 'Web Document'}</div>
                            </div>
                        )}

                        {/* Timestamps */}
                        {documentDetails.update_timestamp && (
                            <div className="detail-item">
                                <div className="detail-label-small">Updated</div>
                                <div className="detail-value-large">{formatDate(documentDetails.update_timestamp)}</div>
                            </div>
                        )}

                        {documentDetails.extracted_timestamp && (
                            <div className="detail-item">
                                <div className="detail-label-small">Extracted</div>
                                <div className="detail-value-large">{formatDate(documentDetails.extracted_timestamp)}</div>
                            </div>
                        )}

                        {/* Error Information */}
                        {documentDetails.extraction_error && (
                            <div className="detail-item">
                                <div className="detail-label-small">Extraction Error</div>
                                <div className="detail-value-large" style={{ color: '#d73a49' }}>{documentDetails.extraction_error}</div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Document Content Section */}
            <div style={{ backgroundColor: 'white', padding: '24px', borderRadius: '8px', border: '1px solid #e0e0e0', boxSizing: 'border-box' }}>
                <h3 style={{ color: '#333', textAlign: 'left', marginBottom: '20px' }}>Document Content</h3>
                <div className="document-content-wrapper">
                    {documentUrl ? (
                        <div className="pdf-viewer-wrapper" style={{ backgroundColor: 'white', width: '100%', height: '700px', border: '1px solid #e0e0e0', borderRadius: '4px' }}>
                            {isPdf ? (
                                <object
                                    data={documentUrl}
                                    type="application/pdf"
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        backgroundColor: 'white'
                                    }}
                                >
                                    <iframe
                                        src={documentUrl}
                                        title={documentTitle}
                                        style={{
                                            backgroundColor: 'white',
                                            width: '100%',
                                            height: '100%',
                                            border: 'none'
                                        }}
                                        sandbox="allow-same-origin allow-scripts allow-forms allow-downloads"
                                        {...(!isInIframe() && { allow: "fullscreen", allowFullScreen: true })}
                                    />
                                </object>
                            ) : (
                                <iframe
                                    src={documentUrl}
                                    title={documentTitle}
                                    style={{
                                        backgroundColor: 'white',
                                        width: '100%',
                                        height: '100%',
                                        border: 'none'
                                    }}
                                    sandbox="allow-same-origin allow-scripts allow-forms allow-downloads"
                                    {...(!isInIframe() && { allow: "fullscreen", allowFullScreen: true })}
                                />
                            )}
                        </div>
                    ) : documentText ? (
                        <div className="document-text-content" style={{ padding: '20px', backgroundColor: 'white', borderRadius: '4px', maxHeight: '600px', overflow: 'auto', border: '1px solid #e0e0e0' }}>
                            <pre style={{ margin: 0, fontFamily: 'monospace', fontSize: '14px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{documentText}</pre>
                        </div>
                    ) : (
                        <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                            <i className="fas fa-file-alt" style={{ fontSize: '48px', marginBottom: '16px', opacity: '0.5' }}></i>
                            <p>No document content available</p>
                        </div>
                    )}
                </div>

                {/* Document Source Information */}
                {(documentDetails?.filename || documentDetails?.url || documentDetails?.content_ref || documentDetails?.download_timestamp || documentDetails?.checksum) && (
                    <div style={{
                        marginTop: '20px',
                        paddingTop: '16px',
                        borderTop: '2px solid #e0e0e0',
                        fontSize: '14px'
                    }}>
                        {documentDetails?.filename && (
                            <div style={{ marginBottom: '8px' }}>
                                <strong style={{ color: '#333' }}>Filename:</strong>{' '}
                                <span style={{ fontFamily: 'monospace', color: '#555' }}>
                                    {documentDetails.filename}
                                </span>
                            </div>
                        )}
                        {documentDetails?.url && (
                            <div style={{ marginBottom: '8px' }}>
                                <strong style={{ color: '#333' }}>Original Source URL:</strong>{' '}
                                <a
                                    href={documentDetails.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        color: '#007bff',
                                        wordBreak: 'break-all',
                                        textDecoration: 'underline'
                                    }}
                                >
                                    {documentDetails.url}
                                </a>
                            </div>
                        )}
                        {documentDetails?.download_timestamp && (
                            <div style={{ marginBottom: '8px' }}>
                                <strong style={{ color: '#333' }}>Downloaded:</strong>{' '}
                                <span style={{ color: '#555' }}>
                                    {formatDate(documentDetails.download_timestamp)}
                                </span>
                            </div>
                        )}
                        {documentDetails?.checksum && (
                            <div>
                                <strong style={{ color: '#333' }}>Checksum:</strong>{' '}
                                <span style={{ fontFamily: 'monospace', color: '#555', fontSize: '13px', wordBreak: 'break-all' }}>
                                    {documentDetails.checksum}
                                </span>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Classification Results Section */}
            {documentDetails?.classification_results && (
                <div style={{
                    marginTop: '20px',
                    backgroundColor: 'white',
                    padding: '20px',
                    borderRadius: '8px',
                    border: '1px solid #e0e0e0'
                }}>
                    <h4 style={{ color: '#333', marginBottom: '16px' }}>
                        Classification Results
                        {documentDetails.classification_experiment_id && (
                            <span style={{ fontSize: '14px', color: '#666', marginLeft: '8px' }}>
                                ({documentDetails.classification_experiment_id})
                            </span>
                        )}
                    </h4>

                    {/* Categories with confidence */}
                    {documentDetails.classification_results.category && (
                        <div style={{ marginBottom: '16px' }}>
                            <strong style={{ color: '#333' }}>Categories:</strong>
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
                                {documentDetails.classification_results.category.map((cat, idx) => (
                                    <span key={idx} style={{
                                        padding: '6px 12px',
                                        backgroundColor: '#e8f5e9',
                                        border: '1px solid #4caf50',
                                        borderRadius: '16px',
                                        fontSize: '14px',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '6px'
                                    }}>
                                        {cat}
                                        {documentDetails.classification_results?.class_probabilities?.[idx] && (
                                            <span style={{
                                                backgroundColor: '#4caf50',
                                                color: 'white',
                                                padding: '2px 6px',
                                                borderRadius: '10px',
                                                fontSize: '12px',
                                                fontWeight: 'bold'
                                            }}>
                                                {(documentDetails.classification_results.class_probabilities[idx] * 100).toFixed(0)}%
                                            </span>
                                        )}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Short description */}
                    {documentDetails.classification_results.short_description && (
                        <div style={{ marginBottom: '16px' }}>
                            <strong style={{ color: '#333' }}>Description:</strong>
                            <p style={{ margin: '8px 0 0 0', color: '#555', lineHeight: '1.6' }}>
                                {documentDetails.classification_results.short_description}
                            </p>
                        </div>
                    )}

                    {/* Plan */}
                    {documentDetails.classification_results.plan && (
                        <div style={{ marginBottom: '16px' }}>
                            <strong style={{ color: '#333' }}>Analysis Plan:</strong>
                            <p style={{ margin: '8px 0 0 0', color: '#555', lineHeight: '1.6' }}>
                                {documentDetails.classification_results.plan}
                            </p>
                        </div>
                    )}

                    {/* Reasoning */}
                    {documentDetails.classification_results.reasoning && documentDetails.classification_results.reasoning.length > 0 && (
                        <div style={{ marginBottom: '16px' }}>
                            <strong style={{ color: '#333' }}>Reasoning:</strong>
                            <ul style={{ margin: '8px 0 0 0', paddingLeft: '24px', color: '#555', lineHeight: '1.6' }}>
                                {documentDetails.classification_results.reasoning.map((reason, idx) => (
                                    <li key={idx} style={{ marginBottom: '8px' }}>{reason}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Metadata */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e0e0e0' }}>
                        {documentDetails.classification_results.language && (
                            <div>
                                <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Language</div>
                                <div style={{ color: '#333' }}>{documentDetails.classification_results.language}</div>
                            </div>
                        )}
                        {documentDetails.classification_results.confidence !== undefined && (
                            <div>
                                <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Confidence</div>
                                <div>
                                    <span style={getConfidenceBadgeStyle(documentDetails.classification_results.confidence)}>
                                        {(documentDetails.classification_results.confidence * 100).toFixed(0)}%
                                    </span>
                                </div>
                            </div>
                        )}
                        {documentDetails.classification_update_timestamp && (
                            <div>
                                <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Updated</div>
                                <div style={{ color: '#333' }}>{formatDate(documentDetails.classification_update_timestamp)}</div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Experiments Section */}
            {documentDetails?.experiments && documentDetails.experiments.length > 0 && (
                <div style={{
                    marginTop: '20px',
                    backgroundColor: 'white',
                    padding: '20px',
                    borderRadius: '8px',
                    border: '1px solid #e0e0e0'
                }}>
                    <h4 style={{ color: '#333', marginBottom: '16px' }}>
                        Experiments ({documentDetails.experiments.length})
                    </h4>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {documentDetails.experiments.map((exp, idx) => (
                            <div key={idx} style={{
                                padding: '12px',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '6px',
                                border: '1px solid #dee2e6',
                                display: 'grid',
                                gridTemplateColumns: '2fr 1fr 1fr',
                                gap: '16px',
                                alignItems: 'center'
                            }}>
                                <div>
                                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Experiment ID</div>
                                    <div style={{ fontFamily: 'monospace', fontSize: '14px', color: '#333' }}>
                                        {exp.experiment_id}
                                    </div>
                                </div>
                                <div>
                                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Model</div>
                                    <div style={{ color: '#333' }}>{exp.model}</div>
                                </div>
                                <div>
                                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Updated</div>
                                    <div style={{ fontSize: '14px', color: '#333' }}>
                                        {formatDate(exp.update_timestamp)}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReportSourceDetailView;